/* eslint-disable no-nested-ternary */
import {
    Formik, FormikHelpers, FormikProps, FormikValues, isEmptyChildren, isFunction,
} from 'formik';
import React from 'react';
import BlockUISpinner from '../../BlockUISpinner';
import FormBanner from '../FormBanner';
import { FormikFormProps } from './types';
import UnsavedFormPrompt from '../UnsavedFormPrompt';
import { removeHidden, validateForm } from '../utils';
import { removeEmptyKeys } from '../../../utils';

const FormikForm = <Values extends FormikValues>(props: FormikFormProps<Values>) => {
    const {
        initialValues,
        isLoading,
        validateSoft,
        validateHard,
        onSubmit,
        promptPath,
        children,
        banner,
        discard,
        hidingFields,
        onSaveAndExit,
        bannerTitle,
        bannerRefTitle,
        bannerSubTitle,
        canSaveDraft,
        showGoToDashboardButton,
        showBanner,
    } = props;

    const validate = async (values: Values) => {
        if (values.saveAndExit && values.saveAndExit === true) {
            if (validateSoft) {
                if (isFunction(validateSoft)) {
                    return validateSoft(values);
                }
                return validateForm(validateSoft, hidingFields || {})(values);
            }
            return {};
        }
        if (validateHard) {
            if (isFunction(validateHard)) {
                return validateHard(values);
            }
            return validateForm(validateHard, hidingFields || {})(values);
        }
        return {};
    };

    const formStatus = {
        hidden: hidingFields,
    };

    const handleSubmit = async (values: Values, formikHelpers: FormikHelpers<Values>) => {
        const removedHidden = removeHidden(values, values, hidingFields || {});
        const normalizedValues = removeEmptyKeys(removedHidden, ([k, _]) => {
            switch (k) {
                case 'saveAndExitClick':
                case 'saveAndExit':
                case 'submitClick':
                    return false;
                default:
                    return true;
            }
        });

        if (values.saveAndExit && values.saveAndExit === true && onSaveAndExit) {
            await onSaveAndExit(normalizedValues, formikHelpers);
        } else {
            await onSubmit(normalizedValues, formikHelpers);
        }
    };

    return (
        <Formik<Values>
            initialValues={initialValues}
            initialStatus={formStatus}
            onSubmit={handleSubmit}
            validate={validate}
            enableReinitialize
        >
            {(formik) => (
                <>
                    {banner !== undefined && !isEmptyChildren(banner)
                        ? React.Children.only(banner)
                        : showBanner
                            ? (
                                <FormBanner
                                    title={bannerTitle}
                                    refTitle={bannerRefTitle}
                                    subTitle={bannerSubTitle}
                                    showSaveAndExitButton={canSaveDraft ?? true}
                                    showGoToDashboardButton={showGoToDashboardButton}
                                    discard={discard}
                                />
                            )
                            : null}
                    {formik.isSubmitting && (
                        <BlockUISpinner>
                            <p>Saving...</p>
                        </BlockUISpinner>
                    )}
                    {isLoading && (
                        <BlockUISpinner>
                            <p>Loading...</p>
                        </BlockUISpinner>
                    )}
                    <UnsavedFormPrompt path={promptPath} />
                    {isFunction(children)
                        ? (children as (bag: FormikProps<Values>) => React.ReactNode)(
                            formik as FormikProps<Values>,
                        )
                        : !isEmptyChildren(children)
                            ? React.Children.only(children)
                            : null}
                </>
            )}
        </Formik>
    );
};

export default FormikForm;
